import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { MultiSelect } from "react-multi-select-component";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import toast from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../../Global";
import NotificationsTable from "./NotificationsTable";
import ScheduledNotificationsTable from "./ScheduledNotificationsTable";

const SCHEDULE_COLLECTION = "lxdc_scheduled_notifications";

// Returns the current local datetime string for the `min` attribute of datetime-local
const getMinDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const Notifications = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [notificationText, setNotificationText] = useState("");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationsList, setNotificationsList] = useState([]);
  const [selectFromMS, setselectFromMS] = useState([]);

  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState("");
  const [scheduledNotificationsList, setScheduledNotificationsList] = useState([]);

  // Ref so the polling interval always reads fresh state without stale closure
  const latestScheduledRef = useRef([]);
  useEffect(() => {
    latestScheduledRef.current = scheduledNotificationsList;
  }, [scheduledNotificationsList]);

  // ── Fetch sent notifications (existing LXDC backend) ────────────────────────
  const fetchUserNotification = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(base_url + "/auth/getNotification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: accessToken,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch notifications");
      const data = await response.json();
      const newList = [
        ...(data?.data?.notifications50Plus || []),
        ...(data?.data?.notifications50Less || []),
      ];
      setNotificationsList(newList);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // ── Fetch scheduled notifications from Firestore ────────────────────────────
  const fetchScheduledNotifications = async () => {
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, SCHEDULE_COLLECTION));
      const list = snap.docs
        .map((d) => {
          const data = d.data();
          const scheduledAt = data.scheduledAt?.toDate
            ? data.scheduledAt.toDate()
            : new Date(data.scheduledAt);
          return {
            id: d.id,
            _scheduledAt: scheduledAt,
            scheduledFor: scheduledAt.toLocaleString(),
            notificationType: data.title || "",
            notificationText: data.body || "",
            userCount: data.userIds?.length ?? 0,
            userIds: data.userIds || [],
            status: data.status || "pending",
          };
        })
        .sort((a, b) => b._scheduledAt - a._scheduledAt);
      setScheduledNotificationsList(list);
    } catch (err) {
      console.error("Error fetching scheduled notifications:", err);
    }
  };

  // ── Send any overdue pending notifications ───────────────────────────────────
  const checkAndSendDue = async () => {
    const list = latestScheduledRef.current;
    const now = new Date();
    const pending = list.filter(
      (n) => n.status === "pending" && n._scheduledAt <= now
    );
    if (pending.length === 0) return;

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    let didSend = false;
    for (const notif of pending) {
      try {
        const res = await fetch(base_url + "/auth/send-notification", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "Bearer " + accessToken,
          },
          body: JSON.stringify({
            notificationType: notif.notificationType,
            userIds: notif.userIds,
            notificationText: notif.notificationText,
          }),
        });
        await loginAdmin();
        await updateDoc(doc(db, SCHEDULE_COLLECTION, notif.id), {
          status: res.ok ? "sent" : "failed",
          sentAt: new Date(),
        });
        if (res.ok) {
          toast.success(`Scheduled notification "${notif.notificationType}" sent!`);
        } else {
          toast.error(`Failed to send scheduled notification: "${notif.notificationType}"`);
        }
        didSend = true;
      } catch (err) {
        console.error("Error sending scheduled notification:", err);
        try {
          await loginAdmin();
          await updateDoc(doc(db, SCHEDULE_COLLECTION, notif.id), { status: "failed" });
        } catch (_) {}
      }
    }
    if (didSend) fetchScheduledNotifications();
  };

  // ── On mount: load data and start polling interval ───────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(base_url + "/auth/allProfile");
        if (!response.ok) throw new Error("Failed to fetch users");
        const data = await response.json();
        const usersWithRequiredFields = data?.users
          ?.filter((user) => !!user.username?.trim())
          ?.map((user) => ({
            value: user._id,
            label: user.username,
            email: user.email || "",
          }));
        setUsers(usersWithRequiredFields);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUserNotification();
    fetchUsers();
    fetchScheduledNotifications();

    // Check every 60 seconds for due scheduled notifications
    const interval = setInterval(checkAndSendDue, 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleNotificationTitle = (e) => {
    const v = e.target.value;
    setNotificationTitle(v.charAt(0).toUpperCase() + v.slice(1));
  };

  const handleNotificationTextChange = (e) => {
    const v = e.target.value;
    setNotificationText(v.charAt(0).toUpperCase() + v.slice(1));
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    const userIdsTemp = selectFromMS.map((item) => item.value);

    if (!notificationTitle || !notificationText) {
      toast.error("Notification title or text is empty");
      setOpen(false);
      return;
    }

    // ── SCHEDULE MODE ──────────────────────────────────────────────────────────
    if (scheduleMode) {
      if (!scheduledDateTime) {
        toast.error("Please select a date and time to schedule");
        setOpen(false);
        return;
      }
      const scheduledAt = new Date(scheduledDateTime);
      if (scheduledAt <= new Date()) {
        toast.error("Scheduled time must be in the future");
        setOpen(false);
        return;
      }
      try {
        await loginAdmin();
        await addDoc(collection(db, SCHEDULE_COLLECTION), {
          title: notificationTitle,
          body: notificationText,
          userIds: userIdsTemp,
          scheduledAt,
          status: "pending",
          createdAt: new Date(),
          platform: "lxdc",
        });
        toast.success("Notification scheduled successfully! 📅");
        setOpen(false);
        setNotificationTitle("");
        setNotificationText("");
        setselectFromMS([]);
        setScheduledDateTime("");
        fetchScheduledNotifications();
      } catch (err) {
        setOpen(false);
        toast.error("Failed to schedule notification");
        console.error(err);
      }
      return;
    }

    // ── IMMEDIATE SEND ─────────────────────────────────────────────────────────
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      toast.error("Not authenticated");
      setOpen(false);
      return;
    }
    try {
      const res = await fetch(base_url + "/auth/send-notification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + accessToken,
        },
        body: JSON.stringify({
          notificationType: notificationTitle,
          userIds: userIdsTemp,
          notificationText,
        }),
      });
      if (res.ok) {
        toast.success("Notification sent successfully");
        setOpen(false);
        setNotificationTitle("");
        setNotificationText("");
        setselectFromMS([]);
        fetchUserNotification();
      } else {
        toast.error("Failed to send notification");
        setOpen(false);
      }
    } catch (err) {
      setOpen(false);
      toast.error("Error sending notification");
      console.error(err);
    }
  };

  const handleDeleteScheduled = async (id) => {
    try {
      await loginAdmin();
      await deleteDoc(doc(db, SCHEDULE_COLLECTION, id));
      toast.success("Scheduled notification cancelled");
      fetchScheduledNotifications();
    } catch (err) {
      toast.error("Failed to cancel notification");
      console.error(err);
    }
  };

  // ── Confirmation dialog label ─────────────────────────────────────────────
  const confirmMessage = scheduleMode
    ? `Schedule this notification for ${scheduledDateTime ? new Date(scheduledDateTime).toLocaleString() : "the selected time"}?`
    : "Do you really want to send this notification now?";

  return (
    <section className="flex-1 rounded-xl w-full md:ml-[19rem] ml-0 mt-[8rem] bg-gray-200 flex flex-col items-start justify-start pt-[2.125rem] px-[2.125rem] pb-[24.625rem] box-border gap-[0.875rem_0rem] md:max-w-[calc(100%_-_300px)] max-w-full text-left text-[1.125rem] text-white font-gilroy mq925:pt-[1.375rem] mq925:pb-[16rem] mq925:box-border mq925:max-w-full mq450:pt-[1.25rem] mq450:pb-[10.375rem] mq450:box-border">

      {/* ── Confirmation Dialog ─────────────────────────────────────────────── */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>{confirmMessage}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">No</Button>
          <Button onClick={handleNotificationSubmit} color="error">Yes</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer className="text-[1rem]" />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col w-full items-end justify-start min-w-[27.125rem] max-w-full text-[1.375rem] mq700:min-w-full">
        <div className="flex items-start w-full justify-between max-w-full text-[1.125rem] mq700:flex-wrap">
          <h1 className="m-0 relative text-inherit leading-[2.375rem] text-[40px] capitalize font-semibold font-inherit inline-block shrink-0 z-[1]">
            Notification
          </h1>
        </div>
      </div>

      {/* ── Title + Users row ──────────────────────────────────────────────── */}
      <div className="self-stretch flex flex-row mt-7 flex-wrap items-start justify-start gap-[0rem_1.5rem] max-w-full">
        {/* Title */}
        <div className="flex-1 flex flex-col items-start justify-start gap-[1.5rem_0rem] min-w-[20.063rem] max-w-full">
          <div className="self-stretch flex flex-col items-start justify-start gap-[0.875rem_0rem]">
            <div className="h-[0.75rem] relative leading-[1.125rem] capitalize flex items-center shrink-0 z-[1]">
              Notification Title
            </div>
            <input
              type="text"
              placeholder="Notification Title"
              value={notificationTitle}
              onChange={handleNotificationTitle}
              className="w-full [border:none] [outline:none] flex justify-between placeholder:text-black self-stretch h-[2.60rem] px-3 rounded-3xs flex flex-row items-start justify-start py-[1rem] px-[1.125rem] box-border font-gilroy font-light text-[1.125rem] text-gray-400 min-w-[15.625rem] z-[1]"
            />
          </div>
        </div>

        {/* Users */}
        <div className="w-full flex-1 flex flex-col items-start justify-start gap-[1.5rem_0rem] min-w-[20.063rem] max-w-[30rem] xl:max-w-[40rem]">
          <div className="w-full self-stretch flex flex-col items-start justify-start gap-[0.875rem_0rem]">
            <div className="w-full h-[0.75rem] relative leading-[1.125rem] capitalize flex items-center shrink-0 z-[1]">
              Select User's
            </div>
            <div className="w-full">
              <MultiSelect
                className="text-black h-[3.375rem] self-stretch"
                options={users}
                value={selectFromMS}
                onChange={setselectFromMS}
                labelledBy="Search and select users..."
                valueRenderer={(selected) =>
                  selected.length
                    ? selected.map(({ label }) => label).join(", ")
                    : "No Items Selected"
                }
                ItemRenderer={({ checked, option, onClick, disabled }) => (
                  <div
                    onClick={onClick}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 4px", cursor: "pointer", opacity: disabled ? 0.4 : 1 }}
                  >
                    <input type="checkbox" checked={checked} onChange={() => {}} style={{ flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: "14px", color: "#1f2937" }}>{option.label}</div>
                      {option.email && (
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>{option.email}</div>
                      )}
                    </div>
                  </div>
                )}
              />
              {selectFromMS.length > 0 && (
                <p style={{ margin: "6px 0 0 2px", fontSize: "13px", color: "#d1d5db" }}>
                  {selectFromMS.length} user{selectFromMS.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Notification Text ─────────────────────────────────────────────── */}
      <div className="self-stretch flex flex-col mt-4 items-start justify-start gap-[0.875rem_0rem]">
        <div className="h-[0.75rem] relative leading-[1.125rem] capitalize flex items-center shrink-0 z-[1]">
          Notification Text
        </div>
        <textarea
          className="w-full [border:none] [outline:none] z-0 self-stretch h-[12.375rem] rounded-3xs flex flex-row items-start justify-start py-[1.313rem] px-[1.125rem] box-border font-gilroy font-light text-[1.125rem] placeholder:text-black text-gray-400 min-w-[15.625rem] z-[1]"
          placeholder="Write Something.."
          value={notificationText}
          onChange={handleNotificationTextChange}
        />
      </div>

      {/* ── Schedule Toggle ───────────────────────────────────────────────── */}
      <div className="self-stretch flex flex-col mt-4 items-start justify-start gap-[0.75rem_0rem]">
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <div
            onClick={() => { setScheduleMode(!scheduleMode); setScheduledDateTime(""); }}
            style={{
              width: "44px",
              height: "24px",
              borderRadius: "12px",
              backgroundColor: scheduleMode ? "#3b82f6" : "#6b7280",
              position: "relative",
              cursor: "pointer",
              transition: "background-color 0.2s",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "3px",
                left: scheduleMode ? "23px" : "3px",
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                backgroundColor: "white",
                transition: "left 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
            />
          </div>
          <span style={{ fontSize: "15px", fontWeight: 500, color: "white" }}>
            {scheduleMode ? "📅 Schedule for later" : "⚡ Send immediately"}
          </span>
        </label>

        {scheduleMode && (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
            <label style={{ fontSize: "13px", color: "white", textTransform: "capitalize" }}>
              Select Date &amp; Time
            </label>
            <input
              type="datetime-local"
              value={scheduledDateTime}
              min={getMinDateTime()}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                border: "none",
                outline: "none",
                fontSize: "15px",
                backgroundColor: "white",
                color: "#1f2937",
                fontFamily: "inherit",
                cursor: "pointer",
                width: "260px",
              }}
            />
            {scheduledDateTime && (
              <p style={{ fontSize: "12px", color: "#d1d5db", margin: 0 }}>
                Will send on: {new Date(scheduledDateTime).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Submit Button ─────────────────────────────────────────────────── */}
      <button
        onClick={() => setOpen(true)}
        style={{
          marginTop: "20px",
          padding: "14px 40px",
          backgroundColor: scheduleMode ? "#3b82f6" : "white",
          color: scheduleMode ? "white" : "#1f2937",
          border: "none",
          borderRadius: "8px",
          fontSize: "16px",
          fontWeight: 600,
          cursor: "pointer",
          textTransform: "capitalize",
          fontFamily: "inherit",
        }}
      >
        {scheduleMode ? "📅 Schedule Notification" : "Submit"}
      </button>

      {/* ── Sent Notifications Table ─────────────────────────────────────── */}
      <div style={{ marginTop: "40px", width: "100%" }}>
        <NotificationsTable notifications={notificationsList} />
      </div>

      {/* ── Scheduled Notifications Table ────────────────────────────────── */}
      <div style={{ width: "100%" }}>
        <ScheduledNotificationsTable
          scheduledNotifications={scheduledNotificationsList}
          onDelete={handleDeleteScheduled}
        />
      </div>
    </section>
  );
};

export default Notifications;
